"use client";

import React, { useEffect, useState } from "react";
import { useTable } from "react-table";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import { EmptyState, LoadingState } from "../UI/States";

const ReviewsTable = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  // Fetch all reviews from the server
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch("/api/cloud/website/getSurveys");
        if (!response.ok) {
          throw new Error("Failed to fetch reviews");
        }
        const data = await response.json();
        setReviews(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setErrorMessage("Unable to load reviews. Please try again.");
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const updateApproval = async (id, status) => {
    try {
      const response = await fetch("/api/cloud/website/approveReview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewId: id, approved: status }),
      });

      if (response.ok) {
        setReviews(
          reviews.map((review) =>
            review._id === id ? { ...review, approved: status } : review
          )
        );
      } else {
        const errorData = await response.json();
        alert(`Failed to update review: ${errorData.error}`);
      }
    } catch (error) {
      console.error("Error updating review approval status:", error);
      alert("An unexpected error occurred.");
    }
  };

  // Table columns
  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Recommend",
        accessor: "recommend",
      },
      {
        Header: "Feedback",
        accessor: "feedback1",
      },
      {
        Header: "Actions",
        Cell: ({ row }) =>
          row.original.approved === 1 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                Approved
              </span>
              <button
                type="button"
                onClick={() => updateApproval(row.original._id, 0)}
                className="app-btn app-btn-secondary app-btn-sm"
              >
                Set to Unapproved
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => updateApproval(row.original._id, 1)}
              className="app-btn app-btn-primary app-btn-sm"
            >
              Approve
            </button>
          ),
      },
    ],
    [reviews]
  );

  const tableInstance = useTable({ columns, data: reviews });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <div className="flex w-full flex-col">
      {loading && <LoadingState label="Loading..." />}
      {errorMessage && (
        <div
          role="alert"
          className="m-3 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
        >
          {errorMessage}
        </div>
      )}
      {!loading && reviews.length === 0 && (
        <EmptyState
          icon={RateReviewRoundedIcon}
          title="No reviews to display."
          compact
        />
      )}
      {!loading && reviews.length > 0 && (
        // The parent (WebsiteReviews) supplies the framed, scrolling surface.
        <table {...getTableProps()} className="app-table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ReviewsTable;
